import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { useState } from 'react';
import { StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import Animated, { LinearTransition, SlideInRight, SlideOutLeft, SlideOutRight } from 'react-native-reanimated';

import { urnBlack } from '@/assets/images';
import { useAuth } from '@/contexts/authContext';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

import BellButton from './buttons/BellButton';
import NotificationButton from './buttons/NotificationButton';
import SearchNpoAutoComplete from './inputs/SearchNpoAutoComplete';
import { SearchBarProps } from './HeaderBar';
import Text from './Text';
import { Button } from './ui/button';

const HeaderBar = ({
  containerStyle,
  showSearch = true,
  showBSText = false,
  screenTitle,
  exitingLeft = false
}: SearchBarProps) => {
  const [isSearching, setIsSearching] = useState(false);
  const colors = useColor();
  const { width: screenWidth } = useWindowDimensions();
  const { state, isBrowseOnlyUser } = useAuth();

  return (
    <View style={[styles.mainNavBarContainer, screenWidth < 768 && { marginVertical: 10 }, containerStyle]}>
      {isSearching && showSearch && (
        <Animated.View
          layout={LinearTransition}
          entering={SlideInRight}
          exiting={exitingLeft ? SlideOutLeft : SlideOutRight}>
          <SearchNpoAutoComplete
            leadingIcon={<Ionicons size={24} name="search" style={styles.iconColor} />}
            onCloseSuggestions={() => setIsSearching(false)}
            variant="rounded_with_shadow"
            onPredictionTap={({ value, label }) => {
              router.navigate({
                pathname: '/npoDetail/[npoId]/insights',
                params: {
                  npoId: value,
                  name: label
                }
              });
            }}
          />
        </Animated.View>
      )}
      {!isSearching && (
        <Animated.View
          layout={LinearTransition}
          style={styles.topBar}
          entering={SlideInRight}
          exiting={exitingLeft ? SlideOutLeft : SlideOutRight}>
          <View style={styles.bsHeader}>
            {showBSText && !screenTitle && (
              <>
                <Image source={urnBlack} style={styles.logo} tintColor={'black'} />
                <Text fontWeight="900" style={styles.title}>
                  {i18n.t('header.bs')}
                </Text>
              </>
            )}
          </View>
          {screenTitle && <Text style={[styles.screenTitle, { color: colors.titleText }]}>{screenTitle}</Text>}
          {showSearch && (
            <TouchableOpacity onPress={() => setIsSearching(!isSearching)}>
              <Ionicons size={24} name="search" style={styles.iconColor} />
            </TouchableOpacity>
          )}
          {state.authReady && !isBrowseOnlyUser && <NotificationButton hitSlop={10} style={styles.rightIcon} />}
          {state.authReady && !isBrowseOnlyUser && <BellButton hitSlop={10} style={styles.rightIcon} />}
          {state.authReady && isBrowseOnlyUser && screenWidth >= 768 && (
            <Button style={styles.rightIcon} variant="default" onClick={() => router.navigate('/')}>
              {capitalize(i18n.t('mix.login'))}
            </Button>
          )}
        </Animated.View>
      )}
    </View>
  );
};

export default HeaderBar;

const styles = StyleSheet.create({
  mainNavBarContainer: {
    zIndex: 1000
  },

  topBar: {
    height: 38,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },

  logo: {
    height: 20,
    width: 20,
    paddingVertical: 20
  },

  title: {
    flex: 1,
    paddingHorizontal: 4,
    fontSize: 14,
    fontWeight: 'bold',
    color: 'black',
    textTransform: 'uppercase'
  },

  screenTitle: {
    flex: 1,
    textTransform: 'capitalize',
    fontWeight: '700',
    fontSize: 20
  },

  iconColor: {
    color: '#808080'
  },

  rightIcon: {
    marginLeft: 15
  },

  bsHeader: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  }
});
